import React, { useEffect, useRef, useState } from "react";
import { MessageCircleMore, Save } from "lucide-react";
import { trackChatCtaClicked, trackDesignLabCtaClicked } from "@design-lab/shared/lib/analytics";
// import { useInactivityDetector } from "@design-lab/shared/lib/useInactivityDetector";
import { Tooltip, TooltipContent, TooltipTrigger } from "@design-lab/shared/ui/tooltip";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { cn, getUserType } from "@design-lab/shared";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { useAtomValue } from "jotai";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useAddToCartModal, useEditCart } from "@design-lab/features";
import { useIsSample } from "@design-lab/shared/lib/config";
import { saveDesignLoadingAtom, addToCartLoadingAtom } from "@design-lab/shared/state";

export function ChatNow() {
  const { isDesktop } = useResponsive();
  const isChatVisible = useRef(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const userType = getUserType();
  const isSample = useIsSample();

  const validation = useValidation();
  const { isEditMode } = useEditMode();
  const { isAddingToCart } = useAddToCartModal();
  const { isEditingCart } = useEditCart();
  const isSaveDesignLoading = useAtomValue(saveDesignLoadingAtom);
  const isAddToCartLoading = useAtomValue(addToCartLoadingAtom);
  const isSubmitting = isEditMode ? isEditingCart : isAddingToCart || isAddToCartLoading;

  const { setIsSaveProjectVisible } = useSaveProject();

  const isSaveDesignDisabled =
    isSubmitting ||
    isSaveDesignLoading ||
    !validation.isArtworkUploaded ||
    validation.isSomeArtworkLoading;

  // Use inactivity detector to show tooltip twice after periods of inactivity
  // useInactivityDetector({
  //   interval: 20_000,
  //   maxCallCount: 2, // Show tooltip only twice
  //   onInactive: () => setShowTooltip(true),
  //   delayDetection: 20_000 // Wait 20 seconds before starting inactivity detection
  // });

  useEffect(() => {
    const handleMinimize = () => {
      window.tidioChatApi?.hide?.();
    };

    // hide the chat widget initially
    const interval = setInterval(() => {
      if (window.tidioChatApi) {
        clearInterval(interval);
        window.tidioChatApi.hide?.();
        isChatVisible.current = false;

        window.tidioChatApi.on("close", handleMinimize);
      }
    }, 500);

    const handleDocumentClick = (event: MouseEvent) => {
      const chatWidget = document.getElementById("tidio-chat");

      if (chatWidget && isChatVisible.current && !chatWidget.contains(event.target as Node)) {
        window.tidioChatApi?.hide?.();
        isChatVisible.current = false;
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      clearInterval(interval);
      document.removeEventListener("click", handleDocumentClick);
      window.tidioChatApi?.off?.("close", handleMinimize);
    };
  }, []);

  const onChatClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Hide tooltip when chat is clicked
    setShowTooltip(false);

    trackChatCtaClicked();

    const chatWidget = document.getElementById("tidio-chat");
    if (chatWidget) {
      isChatVisible.current = true;

      setTimeout(() => {
        window.tidioChatApi?.show?.();
        window.tidioChatApi?.open?.();
        window.tidioChatApi?.messageFromVisitor?.("I need help!");
      }, 100);
    }
  };

  const handleSaveClick = async () => {
    trackDesignLabCtaClicked("save_and_start_new_design");
    setIsSaveProjectVisible(true);
  };

  return (
    <Tooltip open={showTooltip}>
      {!isDesktop && !isEditMode && !isSample && (
        <Save
          className={cn(
            "size-6",
            userType === "guest_user" && "js-sign-in-modal-toggle",
            isSaveDesignDisabled ? "cursor-not-allowed opacity-50" : ""
          )}
          onClick={userType === "guest_user" ? () => {} : handleSaveClick}
        />
      )}
      <TooltipTrigger asChild data-testid="next-button-tooltip-trigger">
        <a
          data-testid="chat-now-link"
          href="#"
          title="Chat with an expert"
          className="group flex flex-row items-center gap-2"
          onClick={onChatClickHandler}
        >
          <MessageCircleMore className="size-6 lg:size-8" />
        </a>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        onClose={() => setShowTooltip(false)}
        onClick={event => {
          setShowTooltip(true);
          onChatClickHandler(event);
        }}
        className="cursor-default bg-blue-700"
        arrowClassName="bg-blue-700 fill-blue-700"
      >
        Need help?
      </TooltipContent>
    </Tooltip>
  );
}
